import { TCopStatusEnum } from '@/features/payments/batch/utils/copStatusHelper';
import { TPayeeVerificationStatus } from '@/features/home/types/PayeeData';

export interface TFieldMapping {
  amount: number;
  payeeName: number;
  bankAccountNumber: number;
  particulars?: number;
  code?: number;
  reference?: number;
}

export interface TBatchPaymentDetail {
  payeeName: string;
  bankAccountNumber: string;
  amount: string;
  particulars?: string;
  code?: string;
  reference?: string;
  payeeVerified?: boolean;
  // Manual batch flow only: the row's client-checked CoP status (from the payee-by-id
  // lookup on selection, or a live verify-details check on manual entry). undefined
  // means "not checked yet"; null means "checked, no known status" - neither blocks
  // submission. Cleared back to undefined whenever the name/account number is edited.
  copStatus?: TCopStatusEnum | null;
  // Manual batch flow only, and only set when the row is bound to a saved payee picked
  // from the search dropdown (never for a hand-typed row or a file-upload row): the
  // payee's id and its verification status at the moment of selection. Drives the
  // "first time paying" confirmation modal at Review - cleared whenever the name/account
  // number is edited, same as payeeVerified/copStatus above.
  payeeId?: string;
  payeeVerificationStatus?: TPayeeVerificationStatus;
}

export interface TBatchPayment {
  id?: string;
  batchName: string;
  walletId: string;
  accountName: string;
  accountNumber: string;
  scheduleDate: Date;
  csvOrBatFile?: File;
  uploadCsvBatchFile: boolean;
  hasHeaders?: boolean;
  fieldMapping?: TFieldMapping;
  paymentDetails?: TBatchPaymentDetail[];
}

export enum BatchPaymentCreationStatusEnum {
  SCHEDULED = 'scheduled',
  PENDING = 'pending',
  WARNING = 'warning',
  IN_PROGRESS = 'in_progress',
  FAILED = 'failed'
}

export enum BatchPaymentPollStatusEnum {
  SCHEDULED = 'scheduled',
  PENDING = 'pending',
  WARNING = 'warning',
  IN_PROGRESS = 'in_progress',
  FAILED = 'failed',
  COMPLETED = 'completed',
  COMPLETED_WITH_FAILURES = 'completed_with_failures',
  EXPIRED = 'expired'
}

export type TBatchPaymentCreationStatus = BatchPaymentCreationStatusEnum;
export type TBatchPaymentPollStatus = BatchPaymentPollStatusEnum;

export interface TAffectedPayment {
  status: TCopStatusEnum | null;
  index: number;
  payeeName: string;
  issue: string;
}

export interface TBatchPaymentWarning {
  title: string;
  message: string;
  code?: string;
  affectedPayments?: TAffectedPayment[];
}

// Shape of the `data` object on a 400 COP_VALIDATION_FAILED error response
// (POST /api/v1/bank/scheduled-payment-groups). Replaces the old success-response
// `warning` payload - the mismatch is now surfaced as an error, not a 2xx warning.
export interface TCopValidationErrorData {
  title: string;
  code: string;
  affectedPayments: TAffectedPayment[];
}

// Backend request detail DTO (maps to Kotlin RqBankPaymentEmergeDetail)
export interface TRqBankPaymentDetail {
  payeeName: string;
  payeeBankAccountNumber: string; // 18-19 chars
  amount: number; // BigDecimal
  payeeParticulars?: string; // max 12 chars
  payeeCode?: string; // max 12 chars
  payeeReference?: string; // max 12 chars
  payeeVerified?: boolean; // True if payee is a known saved account. Default is false which require CoP Verification
}

// Batch payment request (maps to Kotlin RqCreateBankPaymentEmergeGroup and RqCreateScheduledBankPaymentEmergeGroup)
export interface TRqBatchPayment {
  batchName: string; // 1-12 chars
  walletId: string;
  scheduleDate: string; // ISO 8601 with timezone (ZonedDateTime)
  paymentDetails: TRqBankPaymentDetail[]; // 1-100 items
  checkPayeeConfirmation: boolean;
}

// Alias for scheduled batch payment (same structure as immediate)
export type TRqScheduledBatchPayment = TRqBatchPayment;

// Backend response DTO (maps to Kotlin RsScheduledBankPaymentEmergeGroup)
export interface TBatchPaymentResponse {
  status: TBatchPaymentPollStatus;
  id?: string; // batch group ID
  name?: string; // batch name
  batchId?: string; // alias for id (for backwards compatibility)
  message?: string;
  warning?: TBatchPaymentWarning;
  firstPaymentDate?: string; // ISO 8601 ZonedDateTime
  walletId?: string;
  totalAmount?: number; // BigDecimal
  state?: TScheduledBankPaymentGroupState;
  totalPayments?: number;
  completedPayments?: number;
  failedPayments?: number;
  paymentCount?: number; // alias for totalPayments (for backwards compatibility)
  multiSignActionId?: string;
  currentBalance?: number; // actual wallet balance at time of error
  lastPaymentState?: string; // state tracking for mobile compatibility
}

export enum ScheduledBankPaymentGroupStateEnum {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING_APPROVAL = 'pending_approval',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  COMPLETED_WITH_FAILURES = 'completed_with_failures',
  FAILED = 'failed',
  EXPIRED = 'expired'
}

export type TScheduledBankPaymentGroupState = ScheduledBankPaymentGroupStateEnum;

export enum ScheduledBankPaymentGroupTypeEnum {
  XERO = 'xero',
  EMERGE = 'emerge'
}

export type TScheduledBankPaymentGroupType = ScheduledBankPaymentGroupTypeEnum;

// Mirrors the backend's ScheduledPaymentIntervalType (@JsonValue - lowercase on the wire).
// Drives the Type column for individual scheduled payments: ONE_OFF -> "Scheduled",
// anything else -> "Recurring scheduled".
export enum ScheduledPaymentIntervalTypeEnum {
  EVERY_NUMBER_OF_DAYS = 'every_number_of_days',
  EVERY_MONTH_ON_DAY = 'every_month_on_day',
  LAST_DAY_OF_MONTH = 'last_day_of_month',
  ONE_OFF = 'one_off'
}

export type TScheduledPaymentIntervalType = ScheduledPaymentIntervalTypeEnum;

/**
 * Error codes the group delete endpoint can return (see ErrorResponseBody.code), confirmed
 * against DeleteScheduledBankPaymentGroupUseCaseImpl on the backend:
 * - NOT_FOUND (4404): the group id no longer exists (ScheduledBankPaymentGroupNotFoundException,
 *   mapped to the generic SOResponseCode.NotFound rather than a group-specific code) - e.g.
 *   already deleted elsewhere in the meantime. confirmDelete treats this the same as a
 *   successful delete (the desired end state - this row gone - is already true) rather than
 *   showing an error there's nothing left to retry.
 * - DELETION_NOT_ALLOWED (4213): the group was never deletable in this state/type
 *   (ScheduledBankPaymentGroupDeletionNotAllowedException) - wrong type (non-EMERGE), already
 *   force-completed, or outside APPROVED/PENDING_APPROVAL.
 * - DELETION_CONFLICT (4903): a payment in it is due today/overdue and the processing window is
 *   active right now (ScheduledBankPaymentGroupDeletionConflictException).
 * DELETION_NOT_ALLOWED/DELETION_CONFLICT surface the same explanatory modal on the frontend (see
 * UnableToDeleteModal) rather than the generic delete-error banner, since retrying wouldn't help
 * either way - the batch just isn't deletable anymore. Confirmed group-only: an individual
 * scheduled payment's delete (DeactivateScheduledBankPaymentUseCaseImpl) has no equivalent
 * state/window check at all, so these two never apply there - see
 * ScheduledPaymentDeletionErrorCodeEnum (ManagePayments.ts) for its own confirmed codes.
 */
export const BatchDeletionErrorCodeEnum = {
  NOT_FOUND: '4404',
  DELETION_NOT_ALLOWED: '4213',
  DELETION_CONFLICT: '4903'
} as const;
